import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { API, getGeoPosition } from "@/api";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  ClipboardList,
  Package,
  FileText,
  LogOut,
  Menu,
  BookOpen,
  UserCog,
  Image as ImageIcon,
  TrendingUp,
  Settings2,
  Truck,
  Calculator,
  ChevronDown,
  Clock,
  Banknote,
  ArrowRightLeft,
  Ship,
  History,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import welpelLogo from "@/assets/welpel-logo.png";

const ACCOUNTS_MENU = [
  {
    section: "Sales",
    items: [
      { to: "/accounts/sales/performa", label: "Performa Invoice", testid: "nav-acc-performa" },
      { to: "/accounts/sales/invoices", label: "Sale Invoice", testid: "nav-acc-invoices" },
      { to: "/accounts/sales/payment-in", label: "Payment-In", testid: "nav-acc-payment-in" },
      { to: "/accounts/sales/sale-return", label: "Sale Return", testid: "nav-acc-sale-return" },
    ],
  },
  {
    section: "Stock",
    items: [
      { to: "/accounts/stock/purchases", label: "Purchase", testid: "nav-acc-purchases" },
      { to: "/accounts/stock/stock", label: "Stock", testid: "nav-acc-stock" },
      { to: "/accounts/stock/payment-out", label: "Payment-Out", testid: "nav-acc-payment-out" },
    ],
  },
  {
    section: "Accounts",
    items: [
      { to: "/accounts/accounts/expenses", label: "Expenses", testid: "nav-acc-expenses" },
      { to: "/accounts/accounts/parties", label: "Parties", testid: "nav-acc-parties" },
      { to: "/accounts/accounts/cash", label: "Cash In Hand", testid: "nav-acc-cash" },
    ],
  },
  {
    section: "Reports",
    items: [
      { to: "/accounts/reports/pnl", label: "P&L", testid: "nav-acc-pnl" },
      { to: "/accounts/reports/balance-sheet", label: "Balance Sheet", testid: "nav-acc-balance-sheet" },
      { to: "/accounts/reports/profit", label: "Profit (Item/Client)", testid: "nav-acc-profit" },
    ],
  },
];

const NAV = [
  { section: "Overview", to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { section: "Overview", to: "/performance", label: "Performance", icon: TrendingUp, testid: "nav-performance" },
  { section: "CRM", to: "/leads", label: "Leads (CRM)", icon: Users, testid: "nav-leads" },
  { section: "CRM", to: "/customers", label: "Customers", icon: UsersRound, testid: "nav-customers" },
  { section: "Team", to: "/tasks", label: "Tasks", icon: ClipboardList, testid: "nav-tasks" },
  { section: "Team", to: "/attendance", label: "Attendance", icon: Clock, testid: "nav-attendance" },
  { section: "Team", to: "/payroll", label: "Payroll", icon: Banknote, testid: "nav-payroll" },
  { section: "Team", to: "/sops", label: "SOPs", icon: BookOpen, testid: "nav-sops" },
  { section: "Catalog", to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { section: "Catalog", to: "/reorder-report", label: "Next Container", icon: Ship, testid: "nav-reorder-report" },
  { section: "Catalog", to: "/stock-history", label: "Stock History", icon: History, testid: "nav-stock-history" },
  { section: "Catalog", to: "/documents", label: "Quotes & Invoices", icon: FileText, testid: "nav-documents" },
  { section: "Catalog", to: "/media", label: "Media Library", icon: ImageIcon, testid: "nav-media" },
  { section: "Operations", to: "/challans", label: "Delivery Challans", icon: Truck, testid: "nav-challans" },
  { section: "Operations", to: "/transfer-plans", label: "Transfer Plans", icon: ArrowRightLeft, testid: "nav-transfer-plans" },
  { section: "Operations", to: "/accounts", label: "Accounts", icon: Calculator, testid: "nav-accounting", accounts: true },
  { section: "Admin", to: "/team", label: "Team", icon: UserCog, testid: "nav-team" },
  { section: "Admin", to: "/settings", label: "Settings", icon: Settings2, testid: "nav-settings" },
];

function AccountsGroup({ onNavigate }) {
  const location = useLocation();
  const [open, setOpen] = useState(location.pathname.startsWith("/accounts"));
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-accounting"
        className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
          location.pathname.startsWith("/accounts") ? "bg-blue-50 text-blue-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`}
      >
        <Calculator className="h-4 w-4 shrink-0" />
        Accounts
        <ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-2" data-testid="accounts-submenu">
          {ACCOUNTS_MENU.map((g) => (
            <div key={g.section}>
              <div className="px-6 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{g.section}</div>
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center rounded-md py-1.5 pl-9 pr-3 text-[13px] font-medium transition-colors duration-150 ${
                      isActive ? "bg-blue-50 text-blue-800" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarLinks({ onNavigate, items }) {
  const groups = [];
  items.forEach((n) => {
    const g = groups.find((x) => x.section === n.section);
    if (g) g.items.push(n);
    else groups.push({ section: n.section, items: [n] });
  });
  return (
    <nav className="flex flex-col gap-4 overflow-y-auto px-3 pb-4">
      {groups.map((g) => (
        <div key={g.section} data-testid={`nav-section-${g.section.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{g.section}</div>
          <div className="flex flex-col gap-1">
            {g.items.map((n) =>
              n.accounts ? (
                <AccountsGroup key={n.to} onNavigate={onNavigate} />
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                      isActive ? "bg-blue-50 text-blue-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`
                  }
                >
                  <n.icon className="h-4 w-4 shrink-0" />
                  {n.label}
                </NavLink>
              ),
            )}
          </div>
        </div>
      ))}
    </nav>
  );
}

const FIRM2_ROLES = ["admin", "staff", "sales", "accountant"];

function ClockWidget() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    API.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("tf-attendance-changed", refresh);
    return () => window.removeEventListener("tf-attendance-changed", refresh);
  }, [load]);
  if (!FIRM2_ROLES.includes(user?.role)) return null;
  const entry = today?.entry;
  const openShift = entry && !entry.clock_out;
  const fmt = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  const punch = async (kind) => {
    setBusy(true);
    try {
      const body = kind === "in" ? (await getGeoPosition()) || {} : {};
      await API.post(`/attendance/clock-${kind}`, body);
      toast.success(kind === "in" ? "Clocked in — have a great day!" : "Clocked out — see you tomorrow!");
      window.dispatchEvent(new Event("tf-attendance-changed"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="clock-widget">
      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600" data-testid="clock-widget-status">
        <Clock className="h-3.5 w-3.5 text-slate-400" />
        {!entry ? "Not clocked in" : openShift ? `Clocked in ${fmt(entry.clock_in)}` : `Done today · ${entry.hours}h`}
      </div>
      {!entry && (
        <Button size="sm" className="mt-2 h-7 w-full bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => punch("in")} disabled={busy} data-testid="clock-widget-in">Clock In</Button>
      )}
      {openShift && (
        <Button size="sm" className="mt-2 h-7 w-full bg-rose-600 text-xs hover:bg-rose-700" onClick={() => punch("out")} disabled={busy} data-testid="clock-widget-out">Clock Out</Button>
      )}
    </div>
  );
}

function Brand({ testid = "brand-badge" }) {
  return (
    <div className="flex items-center gap-2.5 px-6 py-5">
      <img src={welpelLogo} alt="Welpel" className="h-9 w-9 object-contain" data-testid={testid} />
      <div>
        <div className="font-heading text-base font-bold leading-tight text-slate-900">Welpel</div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-400">B2B Suite</div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const visibleNav = NAV.filter((n) => {
    if (["/reorder-report", "/stock-history"].includes(n.to)) return ["stock_team", "stock_head"].includes(user?.role);
    if (n.to === "/transfer-plans") return ["stock_team", "stock_head"].includes(user?.role);
    if (user?.role === "admin") return n.to !== "/documents";
    if (["/team", "/settings"].includes(n.to)) return false;
    if (n.to === "/payroll") return user?.role === "accountant";
    if (n.to === "/accounts") return user?.role === "accountant";
    if (n.to === "/documents") return !["accountant", "stock_team", "stock_head"].includes(user?.role);
    if (["stock_team", "stock_head"].includes(user?.role)) return ["/", "/inventory", "/tasks", "/challans"].includes(n.to);
    return true;
  });

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const userBlock = (
    <div className="mt-auto border-t border-slate-200 p-4" data-testid="user-menu">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-800">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900">{user?.name}</div>
          <div className="text-xs capitalize text-slate-500">{user?.role}</div>
        </div>
      </div>
      <ClockWidget />
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={doLogout}
        data-testid="logout-button"
      >
        <LogOut className="mr-2 h-3.5 w-3.5" /> Logout
      </Button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <Brand />
        <SidebarLinks items={visibleNav} />
        {userBlock}
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-md lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="mobile-menu-button" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-64 flex-col p-0">
              <Brand testid="brand-badge-mobile" />
              <SidebarLinks onNavigate={() => setOpen(false)} items={visibleNav} />
              {userBlock}
            </SheetContent>
          </Sheet>
          <div className="font-heading text-base font-bold text-slate-900">Welpel</div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
